import React, { useState } from 'react';
import { useWalletStore } from '../store/walletStore';
import * as api from '../api/vexforge';
import { signAndSend, ensureConnected } from '../lib/submit';
import toast from 'react-hot-toast';
import {
  CubeTransparentIcon,
  ShoppingCartIcon,
  BuildingLibraryIcon,
  TruckIcon,
  CircleStackIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

// ── Template Library ──────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'tokens',
    name: 'Tokens',
    icon: CubeTransparentIcon,
    color: 'text-vextaris-blue',
    desc: 'Launch, distribute, and manage fungible tokens',
  },
  {
    id: 'commerce',
    name: 'Commerce',
    icon: ShoppingCartIcon,
    color: 'text-green-400',
    desc: 'Escrow, payments, subscriptions, loyalty programs',
  },
  {
    id: 'governance',
    name: 'Governance',
    icon: BuildingLibraryIcon,
    color: 'text-purple-400',
    desc: 'DAOs, treasuries, multi-sig approval flows',
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain',
    icon: TruckIcon,
    color: 'text-orange-400',
    desc: 'Shipment tracking, custody chains, document attestation',
  },
  {
    id: 'data',
    name: 'Data',
    icon: CircleStackIcon,
    color: 'text-cyan-400',
    desc: 'On-chain attestation, proof anchoring, batch verification',
  },
];

const TEMPLATES = {
  tokens: [
    {
      id: 'launch-with-lp',
      name: 'Launch Token with Liquidity',
      desc: 'Create a token and immediately pair it with VXS in a liquidity pool.',
      steps: [
        { id: 'token', operation: 'CreateToken', label: 'Create fungible token' },
        { id: 'pool', operation: 'CreatePool', label: 'Create VXS liquidity pool' },
      ],
      params: [
        { name: 'token_name', type: 'String', required: true, label: 'Token Name', placeholder: 'My Token' },
        { name: 'token_symbol', type: 'String', required: true, label: 'Symbol', placeholder: 'MTK' },
        { name: 'supply', type: 'Amount', required: true, label: 'Total Supply', placeholder: '1000000000000000000' },
        { name: 'lp_amount', type: 'Amount', required: true, label: 'LP Token Amount', placeholder: '500000000000000000' },
        { name: 'lp_vxs', type: 'Amount', required: true, label: 'LP VXS Pairing', placeholder: '100000000000' },
      ],
    },
    {
      id: 'airdrop',
      name: 'Batch Airdrop',
      desc: 'Distribute tokens to multiple addresses in a single atomic transaction.',
      steps: [
        { id: 'transfers', operation: 'Transfer', label: 'Transfer to each recipient', loop: true },
      ],
      params: [
        { name: 'token', type: 'Address', required: true, label: 'Token Mint Address', placeholder: '0x...' },
        { name: 'recipients', type: 'AddressArray', required: true, label: 'Recipient Addresses (comma-separated)', placeholder: '0xabc...,0xdef...' },
        { name: 'amount_each', type: 'Amount', required: true, label: 'Amount Per Recipient', placeholder: '1000000000' },
      ],
    },
    {
      id: 'vesting',
      name: 'Token Vesting Setup',
      desc: 'Create a multi-sig controlled vesting vault that releases tokens on approval.',
      steps: [
        { id: 'vault', operation: 'CreateMultiSig', label: 'Create vesting vault (multi-sig)' },
        { id: 'fund', operation: 'Transfer', label: 'Fund vault with tokens' },
      ],
      params: [
        { name: 'token', type: 'Address', required: true, label: 'Token Mint', placeholder: '0x...' },
        { name: 'total_amount', type: 'Amount', required: true, label: 'Total Vesting Amount', placeholder: '10000000000000' },
        { name: 'signers', type: 'AddressArray', required: true, label: 'Vault Signers (comma-separated)', placeholder: '0xabc...,0xdef...' },
        { name: 'threshold', type: 'U8', required: true, label: 'Approval Threshold', placeholder: '2' },
      ],
    },
  ],
  commerce: [
    {
      id: 'escrow',
      name: 'Payment Escrow',
      desc: 'Create a 2-of-2 escrow between buyer and seller. Funds release on mutual approval.',
      steps: [
        { id: 'escrow', operation: 'CreateMultiSig', label: 'Create escrow account' },
        { id: 'deposit', operation: 'Transfer', label: 'Deposit payment to escrow' },
      ],
      params: [
        { name: 'buyer', type: 'Address', required: true, label: 'Buyer Address', placeholder: '0x...' },
        { name: 'seller', type: 'Address', required: true, label: 'Seller Address', placeholder: '0x...' },
        { name: 'token', type: 'Address', required: true, label: 'Payment Token', placeholder: '0x... (VXS = all zeros)' },
        { name: 'amount', type: 'Amount', required: true, label: 'Escrow Amount', placeholder: '1000000000' },
      ],
    },
    {
      id: 'loyalty',
      name: 'Loyalty Rewards Program',
      desc: 'Create a multi-token collection for loyalty points with different reward tiers.',
      steps: [
        { id: 'collection', operation: 'CreateMultiTokenCollection', label: 'Create rewards collection' },
        { id: 'tiers', operation: 'DefineTokenType', label: 'Define reward tiers', loop: true },
      ],
      params: [
        { name: 'program_name', type: 'String', required: true, label: 'Program Name', placeholder: 'My Rewards' },
        { name: 'tier_names', type: 'StringArray', required: true, label: 'Tier Names (comma-separated)', placeholder: 'Bronze,Silver,Gold,Platinum' },
      ],
    },
  ],
  governance: [
    {
      id: 'treasury',
      name: 'Treasury with Approval Flow',
      desc: 'Create a multi-sig treasury that requires M-of-N approval for spending.',
      steps: [
        { id: 'treasury', operation: 'CreateMultiSig', label: 'Create treasury multi-sig' },
      ],
      params: [
        { name: 'signers', type: 'AddressArray', required: true, label: 'Treasury Signers', placeholder: '0xabc...,0xdef...,0xghi...' },
        { name: 'threshold', type: 'U8', required: true, label: 'Approval Threshold', placeholder: '2' },
        { name: 'label', type: 'String', required: true, label: 'Treasury Name', placeholder: 'Project Treasury' },
      ],
    },
    {
      id: 'dao-setup',
      name: 'DAO with Token Governance',
      desc: 'Create a DAO where token holders vote on proposals with configurable quorum.',
      steps: [
        { id: 'dao', operation: 'CreateDAO', label: 'Create DAO with governance parameters' },
      ],
      params: [
        { name: 'dao_name', type: 'String', required: true, label: 'DAO Name', placeholder: 'My DAO' },
        { name: 'governance_token', type: 'Address', required: true, label: 'Governance Token', placeholder: '0x...' },
        { name: 'quorum', type: 'U8', required: true, label: 'Quorum %', placeholder: '10' },
        { name: 'threshold', type: 'U8', required: true, label: 'Approval Threshold %', placeholder: '51' },
        { name: 'voting_period', type: 'U64', required: true, label: 'Voting Period (blocks)', placeholder: '7200' },
      ],
    },
  ],
  'supply-chain': [
    {
      id: 'tracked-shipment',
      name: 'Tracked Shipment',
      desc: 'Create a shipment with NFT identity, checkpoint collection, and custody multi-sig.',
      steps: [
        { id: 'shipment', operation: 'CreateNftCollection', label: 'Create shipment NFT' },
        { id: 'checkpoints', operation: 'CreateMultiTokenCollection', label: 'Create checkpoint collection' },
        { id: 'types', operation: 'DefineTokenType', label: 'Define checkpoint types', loop: true },
        { id: 'custody', operation: 'CreateMultiSig', label: 'Create custody chain' },
      ],
      params: [
        { name: 'contents', type: 'String', required: true, label: 'Cargo Description', placeholder: 'Electronics - 500 units' },
        { name: 'parties', type: 'AddressArray', required: true, label: 'Custody Parties', placeholder: '0xshipper...,0xcarrier...,0xcustoms...' },
        { name: 'threshold', type: 'U8', required: true, label: 'Custody Threshold', placeholder: '2' },
        { name: 'checkpoint_types', type: 'U64Array', required: false, label: 'Checkpoint Type IDs', placeholder: '0,1,6,7' },
      ],
    },
    {
      id: 'document-cert',
      name: 'Document Certification',
      desc: 'Anchor a document hash on-chain with metadata for tamper-evident proof.',
      steps: [
        { id: 'anchor', operation: 'Anchor', label: 'Anchor document hash' },
      ],
      params: [
        { name: 'doc_namespace', type: 'String', required: true, label: 'Namespace', placeholder: 'certificates' },
        { name: 'doc_id', type: 'String', required: true, label: 'Document ID', placeholder: 'CERT-2026-001' },
        { name: 'doc_hash', type: 'String', required: true, label: 'BLAKE3 Hash (Vxh or 0x)', placeholder: 'Vxh...' },
        { name: 'metadata_uri', type: 'String', required: false, label: 'Metadata URI', placeholder: 'ipfs://Qm...' },
      ],
    },
  ],
  data: [
    {
      id: 'single-anchor',
      name: 'Anchor Proof',
      desc: 'Write a single hash to the chain as tamper-evident proof. 5,000 gas.',
      steps: [
        { id: 'anchor', operation: 'Anchor', label: 'Anchor data hash' },
      ],
      params: [
        { name: 'namespace', type: 'String', required: true, label: 'Namespace', placeholder: 'myapp' },
        { name: 'key', type: 'String', required: true, label: 'Key (hex)', placeholder: '64617461736574' },
        { name: 'hash', type: 'String', required: true, label: 'BLAKE3 Hash', placeholder: 'Vxh...' },
        { name: 'metadata_uri', type: 'String', required: false, label: 'Metadata URI', placeholder: 'ipfs://Qm...' },
      ],
    },
    {
      id: 'data-collection',
      name: 'Data Collection Registry',
      desc: 'Create a multi-token collection for tracking data entries (datasets, reports, attestations).',
      steps: [
        { id: 'collection', operation: 'CreateMultiTokenCollection', label: 'Create data registry' },
        { id: 'types', operation: 'DefineTokenType', label: 'Define entry types', loop: true },
      ],
      params: [
        { name: 'registry_name', type: 'String', required: true, label: 'Registry Name', placeholder: 'Research Data Registry' },
        { name: 'entry_types', type: 'StringArray', required: true, label: 'Entry Types (comma-separated)', placeholder: 'dataset,report,attestation,model' },
      ],
    },
  ],
};

// ── Build a VIP JSON from template + user values ──────────────

function buildVipJson(template, category, values, walletAddress) {
  const namespace = values._namespace || category;
  const programName = values._program_name || template.id;

  const params = template.params.map((p) => ({
    name: p.name,
    type: p.type,
    required: p.required,
    default_value: null,
    description: p.label,
  }));

  const steps = [];
  for (const stepDef of template.steps) {
    const args = [];

    // Build args based on operation type and template params
    switch (stepDef.operation) {
      case 'Transfer':
        args.push(['to', stepDef.loop ? '${item}' : '${params.recipient}']);
        args.push(['token', '${params.token}']);
        args.push(['amount', '${params.amount_each}']);
        break;
      case 'CreateNftCollection':
        args.push(['name', `Ship-\${params.contents}`]);
        args.push(['symbol', 'SHIP']);
        args.push(['base_uri', '']);
        args.push(['max_supply', '1']);
        args.push(['royalty_bps', '0']);
        args.push(['description', '${params.contents}']);
        args.push(['owner', '${sender}']);
        break;
      case 'CreateMultiTokenCollection':
        args.push(['name', `\${params.${template.params[0]?.name || 'program_name'}}`]);
        args.push(['symbol', 'VIP']);
        args.push(['base_uri', '']);
        args.push(['royalty_bps', '0']);
        args.push(['description', `VIP: ${template.name}`]);
        args.push(['owner', '${sender}']);
        break;
      case 'DefineTokenType':
        args.push(['collection', `\${steps.${template.steps.find(s => s.operation === 'CreateMultiTokenCollection')?.id || 'collection'}.result.collection}`]);
        args.push(['type_id', '${item}']);
        args.push(['name', 'Type-${item}']);
        args.push(['metadata_uri', '']);
        args.push(['is_fungible', 'true']);
        break;
      case 'CreateMultiSig':
        args.push(['signers', '${params.signers}']);
        args.push(['threshold', `\${params.${template.params.find(p => p.name === 'threshold')?.name || 'threshold'}}`]);
        args.push(['label', `\${params.${template.params.find(p => p.type === 'String')?.name || 'label'}}`]);
        break;
      case 'CreatePool':
        args.push(['token_a', '${steps.token.result.address}']);
        args.push(['token_b', '0x0000000000000000000000000000000000000000000000000000000000000000']);
        args.push(['amount_a', '${params.lp_amount}']);
        args.push(['amount_b', '${params.lp_vxs}']);
        break;
      case 'Anchor':
        args.push(['namespace', '${params.namespace}']);
        args.push(['key', '${params.key}']);
        args.push(['value_hash', '${params.hash}']);
        args.push(['metadata_uri', '${params.metadata_uri}']);
        break;
      case 'CreateDAO':
        args.push(['name', '${params.dao_name}']);
        args.push(['governance_token', '${params.governance_token}']);
        args.push(['quorum', '${params.quorum}']);
        args.push(['threshold', '${params.threshold}']);
        args.push(['voting_period', '${params.voting_period}']);
        break;
      default:
        break;
    }

    const loopParam = stepDef.loop
      ? `params.${template.params.find(p => p.type.includes('Array'))?.name || 'items'}`
      : null;

    steps.push({
      id: stepDef.id,
      operation: stepDef.operation,
      args,
      loop_over: loopParam,
    });
  }

  return {
    namespace,
    name: programName,
    version: 1,
    author: walletAddress || '0x0000000000000000000000000000000000000000000000000000000000000000',
    description: template.desc,
    params,
    steps,
    returns: [],
    registered_at: 0,
    safety_score: 0,
    safety_level: 'Standard',
    flag_count: 0,
    invocation_count: 0,
  };
}

// ── Components ────────────────────────────────────────────────

function CategoryCard({ category, selected, onClick }) {
  const Icon = category.icon;
  return (
    <button
      onClick={onClick}
      className={`text-left p-5 rounded-xl border transition-all ${
        selected
          ? 'bg-vextaris-dark border-vextaris-purple shadow-lg shadow-vextaris-purple/10'
          : 'bg-vextaris-darker border-gray-800 hover:border-gray-700'
      }`}
    >
      <Icon className={`w-6 h-6 ${category.color} mb-3`} />
      <h3 className="text-white font-medium">{category.name}</h3>
      <p className="text-gray-500 text-xs mt-1">{category.desc}</p>
    </button>
  );
}

function TemplateCard({ template, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left w-full p-4 rounded-xl border transition-all ${
        selected
          ? 'bg-vextaris-dark border-vextaris-purple'
          : 'bg-vextaris-darker border-gray-800 hover:border-gray-700'
      }`}
    >
      <div className="flex justify-between items-start">
        <h4 className="text-white font-medium">{template.name}</h4>
        <span className="text-gray-600 text-xs">{template.steps.length} step{template.steps.length > 1 ? 's' : ''}</span>
      </div>
      <p className="text-gray-500 text-sm mt-1">{template.desc}</p>
    </button>
  );
}

function SafetyBadge({ level, score }) {
  const colors = {
    Verified: 'bg-green-500/20 text-green-400 border-green-500/30',
    Standard: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Warning: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Blocked: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors[level] || colors.Standard}`}>
      <ShieldCheckIcon className="w-3 h-3 mr-1" />
      {level} ({score}/100)
    </span>
  );
}

// ── Client-Side Safety Assessment ─────────────────────────────

const ALLOWED_OPS = [
  'Transfer', 'CreateToken', 'CreateNftCollection', 'MintNft', 'TransferNft', 'BurnNft',
  'CreateMultiTokenCollection', 'DefineTokenType', 'MintMultiToken', 'BatchMintMultiToken',
  'TransferMultiToken', 'BurnMultiToken', 'UpdateTokenMetadata', 'LockTokenMetadata',
  'CreatePool', 'AddLiquidity', 'Swap', 'CreateMultiSig', 'ProposeMultiSigTx',
  'CreateStakingPool', 'FundStakingPool', 'StakeToPool', 'Stake', 'Delegate',
  'RegisterName', 'Anchor', 'CreateProposal', 'CreateDAO',
];

const RESERVED_NS = ['vexidus', 'vex', 'foundation', 'system', 'core'];

function computeLocalSafetyScore(program) {
  let score = 50;
  const flags = [];

  // Check namespace
  if (!program.namespace || program.namespace.length < 2 || program.namespace.length > 32) {
    score -= 50; flags.push({ severity: 'Critical', message: 'Namespace must be 2-32 characters' });
  }
  if (RESERVED_NS.includes(program.namespace)) {
    score -= 30; flags.push({ severity: 'Warning', message: `Namespace "${program.namespace}" is reserved` });
  }

  // Check operations are allowed
  for (const step of (program.steps || [])) {
    if (!ALLOWED_OPS.includes(step.operation)) {
      score -= 50; flags.push({ severity: 'Critical', message: `Operation "${step.operation}" is not allowed in VIPs` });
    }
  }

  // Check step count
  if ((program.steps || []).length > 20) {
    score -= 20; flags.push({ severity: 'Warning', message: 'More than 20 steps may hit expansion limits' });
  }
  if ((program.steps || []).length === 0) {
    score -= 50; flags.push({ severity: 'Critical', message: 'Program has no steps' });
  }

  // Check params
  if ((program.params || []).length > 20) {
    score -= 10; flags.push({ severity: 'Warning', message: 'Large number of parameters (>20)' });
  }

  // Check for hardcoded recipients (args that look like raw addresses)
  for (const step of (program.steps || [])) {
    for (const [, val] of (step.args || [])) {
      if (typeof val === 'string' && val.startsWith('0x') && val.length === 66 && !val.includes('${')) {
        const allZeros = val === '0x0000000000000000000000000000000000000000000000000000000000000000';
        if (!allZeros) {
          score -= 15; flags.push({ severity: 'Warning', message: `Step "${step.id}" has hardcoded address recipient` });
        }
      }
    }
  }

  // Bonus for using template expressions
  let usesTemplates = false;
  for (const step of (program.steps || [])) {
    for (const [, val] of (step.args || [])) {
      if (typeof val === 'string' && val.includes('${')) usesTemplates = true;
    }
  }
  if (usesTemplates) score += 20;

  // Description bonus
  if (program.description && program.description.length > 10) score += 10;

  const clamped = Math.max(0, Math.min(100, score));
  const level = clamped >= 80 ? 'Verified' : clamped >= 40 ? 'Standard' : clamped >= 20 ? 'Warning' : 'Blocked';

  return { score: clamped, level, flags, blocking: level === 'Blocked' };
}

// ── Main Library Component ────────────────────────────────────

export default function VipLibrary() {
  const { address, isConnected } = useWalletStore();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [wizardStep, setWizardStep] = useState(0); // 0=params, 1=safety, 2=review, 3=done
  const [paramValues, setParamValues] = useState({});
  const [namespace, setNamespace] = useState('');
  const [programName, setProgramName] = useState('');
  const [generatedJson, setGeneratedJson] = useState(null);
  const [safetyResult, setSafetyResult] = useState(null);
  const [checkingSafety, setCheckingSafety] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setSelectedTemplate(null);
    setWizardStep(0);
    setParamValues({});
    setGeneratedJson(null);
    setSafetyResult(null);
    setResult(null);
    setNamespace('');
    setProgramName('');
  };

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setWizardStep(0);
    setParamValues({});
    setGeneratedJson(null);
    setResult(null);
    setNamespace(selectedCategory || '');
    setProgramName(tmpl.id);
  };

  const handleGenerate = async () => {
    const json = buildVipJson(
      selectedTemplate,
      namespace || selectedCategory,
      { ...paramValues, _namespace: namespace, _program_name: programName },
      address
    );
    json.namespace = namespace || selectedCategory;
    json.name = programName || selectedTemplate.id;
    json.author = address || json.author;
    setGeneratedJson(json);

    // Run safety check
    setCheckingSafety(true);
    setSafetyResult(null);
    setWizardStep(1);
    try {
      const safety = await api.getProgramSafety(json.namespace, json.name).catch(() => null);
      // If program doesn't exist yet (it shouldn't), simulate from the JSON definition
      // The safety analysis runs on the program structure, not on-chain state
      // For now, compute a client-side safety assessment based on the template
      const score = computeLocalSafetyScore(json);
      setSafetyResult(score);
    } catch {
      // Fallback: local safety assessment
      setSafetyResult(computeLocalSafetyScore(json));
    }
    setCheckingSafety(false);
  };

  const handleRegister = async () => {
    if (safetyResult && safetyResult.level === 'Blocked') return toast.error('Safety check failed. Fix issues before registering.');
    if (!isConnected) { await ensureConnected(); return; }
    setSubmitting(true);
    try {
      const txHash = await signAndSend(
        api.registerProgramOps(JSON.stringify(generatedJson)),
        `Register VIP ${generatedJson?.namespace}/${generatedJson?.name}`,
      );
      if (txHash) {
        setResult({ success: true, txHash });
        setWizardStep(3);
        toast.success('VIP registration submitted!');
      }
    } catch (err) {
      toast.error(err.message);
      setResult({ success: false, error: err.message });
    }
    setSubmitting(false);
  };

  // ── Category Selection ──────────────────────────────────
  if (!selectedCategory) {
    return (
      <div>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">What do you want to build?</h2>
          <p className="text-gray-400">Pick a category, choose a template, customize the parameters, deploy.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map((cat) => (
            <CategoryCard
              key={cat.id}
              category={cat}
              selected={false}
              onClick={() => setSelectedCategory(cat.id)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Template Selection ──────────────────────────────────
  if (!selectedTemplate) {
    const templates = TEMPLATES[selectedCategory] || [];
    const category = CATEGORIES.find((c) => c.id === selectedCategory);
    const Icon = category?.icon || CubeTransparentIcon;

    return (
      <div>
        <button
          onClick={() => setSelectedCategory(null)}
          className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to categories
        </button>

        <div className="flex items-center space-x-3 mb-6">
          <Icon className={`w-6 h-6 ${category?.color}`} />
          <h2 className="text-2xl font-bold text-white">{category?.name} Templates</h2>
        </div>

        <div className="space-y-3">
          {templates.map((tmpl) => (
            <TemplateCard
              key={tmpl.id}
              template={tmpl}
              selected={false}
              onClick={() => handleSelectTemplate(tmpl)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Wizard: Configure Parameters ────────────────────────
  if (wizardStep === 0) {
    return (
      <div>
        <button
          onClick={reset}
          className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to templates
        </button>

        <h2 className="text-2xl font-bold text-white mb-2">{selectedTemplate.name}</h2>
        <p className="text-gray-400 text-sm mb-6">{selectedTemplate.desc}</p>

        {/* Steps preview */}
        <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-4 mb-6">
          <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Operations this VIP will execute</h4>
          <div className="space-y-2">
            {selectedTemplate.steps.map((s, i) => (
              <div key={i} className="flex items-center space-x-3 text-sm">
                <span className="text-vextaris-purple font-mono w-5 text-right">{i + 1}.</span>
                <span className="text-white">{s.label}</span>
                <span className="text-gray-600 text-xs">({s.operation})</span>
                {s.loop && <span className="text-yellow-500 text-xs">loop</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Namespace + Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Namespace</label>
            <input
              type="text"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder={selectedCategory}
              className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Program Name</label>
            <input
              type="text"
              value={programName}
              onChange={(e) => setProgramName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder={selectedTemplate.id}
              className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
        </div>

        {/* Parameter inputs */}
        <div className="space-y-4 mb-8">
          <h3 className="text-white font-medium">Parameters</h3>
          {selectedTemplate.params.map((p) => (
            <div key={p.name}>
              <label className="block text-sm text-gray-400 mb-1">
                {p.label}
                {p.required && <span className="text-red-400 ml-1">*</span>}
                <span className="text-gray-600 ml-2 text-xs">({p.type})</span>
              </label>
              {p.type === 'AddressArray' || p.type === 'StringArray' || p.type === 'U64Array' ? (
                <textarea
                  value={paramValues[p.name] || ''}
                  onChange={(e) => setParamValues({ ...paramValues, [p.name]: e.target.value })}
                  placeholder={p.placeholder}
                  className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple h-20 resize-none font-mono text-sm"
                />
              ) : (
                <input
                  type="text"
                  value={paramValues[p.name] || ''}
                  onChange={(e) => setParamValues({ ...paramValues, [p.name]: e.target.value })}
                  placeholder={p.placeholder}
                  className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple"
                />
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleGenerate}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          <span>Run Safety Check</span>
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ── Wizard: Safety Check ─────────────────────────────────
  if (wizardStep === 1) {
    return (
      <div>
        <button
          onClick={() => setWizardStep(0)}
          className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to parameters
        </button>

        <h2 className="text-2xl font-bold text-white mb-2">Safety Check</h2>
        <p className="text-gray-400 text-sm mb-6">Your VIP must pass safety analysis before it can be registered on-chain.</p>

        {checkingSafety ? (
          <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vextaris-purple mx-auto mb-4"></div>
            <p className="text-gray-400">Analyzing program safety...</p>
          </div>
        ) : safetyResult ? (
          <div>
            {/* Score Display */}
            <div className={`border rounded-xl p-6 mb-6 ${
              safetyResult.level === 'Verified' ? 'bg-green-500/5 border-green-500/30' :
              safetyResult.level === 'Standard' ? 'bg-yellow-500/5 border-yellow-500/30' :
              safetyResult.level === 'Warning' ? 'bg-orange-500/5 border-orange-500/30' :
              'bg-red-500/5 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <ShieldCheckIcon className={`w-8 h-8 ${
                    safetyResult.level === 'Verified' ? 'text-green-400' :
                    safetyResult.level === 'Standard' ? 'text-yellow-400' :
                    safetyResult.level === 'Warning' ? 'text-orange-400' :
                    'text-red-400'
                  }`} />
                  <div>
                    <h3 className="text-white text-lg font-semibold">Safety Score: {safetyResult.score}/100</h3>
                    <p className={`text-sm ${
                      safetyResult.level === 'Verified' ? 'text-green-400' :
                      safetyResult.level === 'Standard' ? 'text-yellow-400' :
                      safetyResult.level === 'Warning' ? 'text-orange-400' :
                      'text-red-400'
                    }`}>{safetyResult.level}</p>
                  </div>
                </div>
                {!safetyResult.blocking && (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm font-medium">
                    PASS
                  </span>
                )}
                {safetyResult.blocking && (
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full text-sm font-medium">
                    FAIL
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    safetyResult.score >= 80 ? 'bg-green-500' :
                    safetyResult.score >= 40 ? 'bg-yellow-500' :
                    safetyResult.score >= 20 ? 'bg-orange-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${safetyResult.score}%` }}
                />
              </div>
            </div>

            {/* Flags */}
            {safetyResult.flags.length > 0 && (
              <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-5 mb-6">
                <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Analysis Flags</h4>
                <div className="space-y-2">
                  {safetyResult.flags.map((flag, i) => (
                    <div key={i} className={`flex items-start space-x-3 text-sm ${
                      flag.severity === 'Critical' ? 'text-red-400' :
                      flag.severity === 'Warning' ? 'text-orange-400' :
                      'text-gray-400'
                    }`}>
                      <span className="font-mono text-xs mt-0.5">{flag.severity === 'Critical' ? 'CRIT' : flag.severity === 'Warning' ? 'WARN' : 'INFO'}</span>
                      <span>{flag.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-4">
              {!safetyResult.blocking ? (
                <button
                  onClick={() => setWizardStep(2)}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <span>Proceed to Review</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setWizardStep(0)}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Go Back and Fix Issues</span>
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // ── Wizard: Review & Register ───────────────────────────
  if (wizardStep === 2) {
    return (
      <div>
        <button
          onClick={() => setWizardStep(1)}
          className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to safety check
        </button>

        <h2 className="text-2xl font-bold text-white mb-6">Review Your VIP</h2>

        {/* Safety badge */}
        {safetyResult && (
          <div className="mb-4">
            <SafetyBadge level={safetyResult.level} score={safetyResult.score} />
          </div>
        )}

        {/* Summary */}
        <div className="bg-vextaris-darker border border-gray-800 rounded-xl p-5 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-white text-lg font-semibold">
                <span className="text-gray-400">{generatedJson?.namespace}/</span>
                {generatedJson?.name}
              </h3>
              <p className="text-gray-400 text-sm mt-1">{generatedJson?.description}</p>
            </div>
            <span className="text-gray-600 text-xs">{generatedJson?.steps?.length} steps</span>
          </div>
        </div>

        {/* Generated JSON (collapsible) */}
        <details className="mb-6">
          <summary className="text-gray-400 text-sm cursor-pointer hover:text-white transition-colors">
            View generated JSON
          </summary>
          <pre className="mt-2 bg-vextaris-dark border border-gray-800 rounded-xl p-4 text-green-400 font-mono text-xs overflow-auto max-h-96">
            {JSON.stringify(generatedJson, null, 2)}
          </pre>
        </details>

        {/* Register button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleRegister}
            disabled={submitting}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-vextaris-dark rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <ShieldCheckIcon className="w-5 h-5" />
            <span>{submitting ? 'Registering...' : 'Register VIP (50 VXS)'}</span>
          </button>
          {!isConnected && (
            <span className="text-gray-500 text-sm">Connect wallet to register</span>
          )}
        </div>

        {result?.success === false && (
          <div className="mt-4 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
            <p className="text-red-400 text-sm">{result.error}</p>
          </div>
        )}
      </div>
    );
  }

  // ── Wizard: Success ─────────────────────────────────────
  if (wizardStep === 3) {
    return (
      <div className="text-center py-12">
        <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">VIP Registered</h2>
        <p className="text-gray-400 mb-2">
          <span className="text-white">{generatedJson?.namespace}/{generatedJson?.name}</span> is now live on Vextaris.
        </p>
        <p className="text-gray-600 text-xs mb-8">Bundle: {result?.txHash}</p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => { reset(); setSelectedCategory(null); }}
            className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg hover:border-gray-500 transition-colors"
          >
            Build Another
          </button>
        </div>
      </div>
    );
  }

  return null;
}
